/**
 * @module Model.FacilityModel
 *
 */
import { Model } from "typedefs";
import { CoachProfileModel, CoachProfileResponse } from 'app/models/scheduling/CoachProfileModel';

interface Address extends Model {
  street1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface FeatureFlags {
  adminsCanEditCoachSettings?: boolean;
  facilityLevelBookingOptions?: boolean;
  bookingOptionsCapacity?: boolean;
  facilityLevelStripeIntegration?: boolean;
  resourceManagement?: boolean;
  schedulingInterval?: number | null;
  [key: string]: boolean | number | null | undefined;
}

export interface FacilityResponse extends Model {
  id: string;
  /**
   * CFTB-3437: the facility's own identifier in the scheduling service, distinct
   * from `id` (which is its external id from the core app). The public programmes
   * index looks a facility up by this one, and the public facility URL carries it.
   */
  uuid?: string;
  name: string;
  coachProfiles?: CoachProfileResponse[];
  address?: Address;
  currency?: string;
  timeZone?: string;
  publicLogoUrl?: string;
  publicCoverUrl?: string;
  allowAdditionalPeople?: boolean;
  featureFlags?: FeatureFlags;
  stripeAccountId?: string | null;
  stripeIntegrationEnabled?: boolean;
  stripeAccountActive?: boolean;
}

export class FacilityModel implements Model {
  id: string;
  /** See `FacilityResponse` — the service's own id, not the core app's. */
  uuid?: string;
  name: string;
  coachProfiles?: CoachProfileModel[];
  address?: string;
  currency?: string;
  timeZone?: string;
  publicCalendarURL?: string;
  publicCalendar?: boolean;
  publicProgramsURL?: string;
  publicPrograms?: boolean;
  publicLandingPageURL?: string;
  publicLandingPage?: boolean;
  logo_url?: string;
  public_logo_url?: string;
  public_cover_url?: string;
  allowAdditionalPeople?: boolean;
  featureFlags?: FeatureFlags;
  stripeAccountId?: string | null;
  stripeIntegrationEnabled?: boolean;
  stripeAccountActive?: boolean;

  get adminsCanEditCoachSettings(): boolean {
    return this.featureFlags?.adminsCanEditCoachSettings === true;
  }

  get hasFacilityLevelStripe(): boolean {
    return this.featureFlags?.facilityLevelStripeIntegration === true;
  }

  constructor(facility: FacilityResponse) {
    this.id = facility.id;
    this.uuid = facility.uuid;
    this.name = facility.name;
    this.currency = facility.currency;
    this.timeZone = facility.timeZone;
    this.publicCalendarURL = facility.publicCalendarUrl;
    this.publicCalendar = facility.publicCalendar;
    this.publicPrograms = facility.publicPrograms;
    this.publicProgramsURL = facility.publicProgramsUrl;
    this.publicLandingPage = facility.publicLandingPage;
    this.publicLandingPageURL = facility.publicLandingPageUrl;
    this.logo_url = facility.logoUrl;
    this.public_logo_url = facility.publicLogoUrl;
    this.public_cover_url = facility.publicCoverUrl;
    this.allowAdditionalPeople = facility.allowAdditionalPeople;
    this.featureFlags = facility.featureFlags;
    this.stripeAccountId = facility.stripeAccountId;
    this.stripeIntegrationEnabled = facility.stripeIntegrationEnabled;
    this.stripeAccountActive = facility.stripeAccountActive;
    if (facility?.address) {
      this.address =
        facility?.address?.street1 +
        ", " +
        facility?.address?.street2 +
        ", " +
        facility?.address?.city +
        ", " +
        facility?.address?.state +
        ", " +
        facility?.address?.zip +
        ", " +
        facility?.address?.country;
    }
    if (facility.coachProfiles && facility.coachProfiles.length > 0) {
      this.coachProfiles = facility.coachProfiles.map(c => new CoachProfileModel(c));
    }
  }
}
